/**
 * Presentación dueño de fases PVP y estados de retención.
 *
 * Los códigos del backend (`symptom` / `critical` / etc.) no cambian.
 * Solo labels, tips y colores alineados al Dashboard (Bien / Atención / Crítico).
 *
 * Ver `docs/PVP_STATUS_OWNER_LANGUAGE.md`.
 */

import type { PvpPhase } from '@/application/metrics/metricsTypes';
import type { RetentionStatus } from '@/application/metrics/ownerPvpRetentionTypes';

/** Semántica dueño (= monthVerdict). */
export type OwnerStatusTone = 'good' | 'watch' | 'critical' | 'neutral';

export type OwnerStatusPresentation = {
  tone: OwnerStatusTone;
  /** i18n key for short badge label. */
  labelKey: string;
  /** i18n key for “qué significa”. */
  tipKey: string;
};

const PHASE: Record<PvpPhase, OwnerStatusPresentation> = {
  symptom: {
    tone: 'critical',
    labelKey: 'metrics.ownerStatus.phase.symptom',
    tipKey: 'metrics.ownerStatus.phaseTip.symptom',
  },
  correction: {
    tone: 'watch',
    labelKey: 'metrics.ownerStatus.phase.correction',
    tipKey: 'metrics.ownerStatus.phaseTip.correction',
  },
  wellness: {
    tone: 'good',
    labelKey: 'metrics.ownerStatus.phase.wellness',
    tipKey: 'metrics.ownerStatus.phaseTip.wellness',
  },
};

const RETENTION: Record<RetentionStatus, OwnerStatusPresentation> = {
  critical: {
    tone: 'critical',
    labelKey: 'metrics.ownerStatus.retention.critical',
    tipKey: 'metrics.ownerStatus.retentionTip.critical',
  },
  alert: {
    tone: 'watch',
    labelKey: 'metrics.ownerStatus.retention.alert',
    tipKey: 'metrics.ownerStatus.retentionTip.alert',
  },
  observation: {
    tone: 'watch',
    labelKey: 'metrics.ownerStatus.retention.observation',
    tipKey: 'metrics.ownerStatus.retentionTip.observation',
  },
  normal: {
    tone: 'good',
    labelKey: 'metrics.ownerStatus.retention.normal',
    tipKey: 'metrics.ownerStatus.retentionTip.normal',
  },
  insufficient_history: {
    tone: 'neutral',
    labelKey: 'metrics.ownerStatus.retention.insufficient_history',
    tipKey: 'metrics.ownerStatus.retentionTip.insufficient_history',
  },
};

/** Labels ES para export PDF/email (sin i18n en el builder). */
const PHASE_EXPORT_ES: Record<PvpPhase, string> = {
  symptom: 'Crítico (0–14)',
  correction: 'Atención (15–29)',
  wellness: 'Bien (30+)',
};

export function presentPvpPhase(phase: PvpPhase): OwnerStatusPresentation {
  return PHASE[phase];
}

export function presentRetentionStatus(status: RetentionStatus): OwnerStatusPresentation {
  return RETENTION[status];
}

/**
 * Estado del listado hub/ranking: solo el PVP real (el mismo que el detalle).
 * Si no vino de la API, no inventar Bien ni Atención.
 */
export function listedRetentionStatus(
  fromApi: RetentionStatus | undefined
): RetentionStatus | null {
  return fromApi ?? null;
}

/** Filtro de ranking dueño: una opción por tono de badge (no agrupa Crítico con Atención). */
export type OwnerStatusFilter = 'all' | 'critical' | 'attention' | 'ok';

export function matchesOwnerStatusFilter(
  status: RetentionStatus,
  filter: OwnerStatusFilter
): boolean {
  if (filter === 'all') return true;
  if (filter === 'critical') return status === 'critical';
  if (filter === 'attention') return status === 'alert' || status === 'observation';
  return status === 'normal';
}

export function resolvePvpPhaseFromValue(value: number): PvpPhase {
  if (value < 15) return 'symptom';
  if (value < 30) return 'correction';
  return 'wellness';
}

export function pvpPhaseExportLabelEs(phase: string): string {
  if (phase === 'symptom' || phase === 'correction' || phase === 'wellness') {
    return PHASE_EXPORT_ES[phase];
  }
  return phase;
}

export function ownerStatusBadgeClass(tone: OwnerStatusTone): string {
  if (tone === 'critical') {
    return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200';
  }
  if (tone === 'watch') {
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
  }
  if (tone === 'neutral') {
    return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200';
  }
  return 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200';
}

/** Tone for MetricKpiCard. */
export function ownerStatusKpiTone(tone: OwnerStatusTone): 'green' | 'amber' | 'red' | 'blue' {
  if (tone === 'critical') return 'red';
  if (tone === 'watch') return 'amber';
  if (tone === 'neutral') return 'blue';
  return 'green';
}
